import { Link } from "react-router-dom";
import { ArrowUpRight, UserCheck, LineChart, ShieldAlert, Building2 } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, CTABand } from "../components/PageParts";
import SectionHeading from "../components/SectionHeading";
import ProcessFlow from "../components/ProcessFlow";
import RiskNote from "../components/RiskNote";
import { FAQ } from "../components/Cards";
import { FAQS, MASTER_TRADERS } from "../config/site";

const STEPS = [
  {
    title: "Browse master profiles",
    desc: "Review each master trader's profile, style and the performance and risk information the host exchange publishes.",
  },
  {
    title: "Open the exchange page",
    desc: "Follow the external link to the master's page on the supported exchange — the destination is always shown.",
  },
  {
    title: "Allocate on the exchange",
    desc: "If you choose to follow, you allocate directly through the exchange's copy-trading platform, in your own account.",
  },
  {
    title: "Stay in control",
    desc: "Your funds never leave your exchange account. Stop following at any time through the exchange's interface.",
  },
];

const FEATURES = [
  {
    icon: UserCheck,
    title: "Master trader profiles",
    desc: "Each listed master carries a profile with trading style, focus markets and the exchange it operates on.",
  },
  {
    icon: LineChart,
    title: "Published performance & risk data",
    desc: "Where the host exchange publishes performance and risk information for a master, we link to it — unmodified and with its period stated.",
  },
  {
    icon: Building2,
    title: "Supported exchange details",
    desc: "Every entry states its host exchange, so you always know exactly which platform's terms and custody you are relying on.",
  },
  {
    icon: ShieldAlert,
    title: "Non-custodial by design",
    desc: "Following happens through the exchange. Kalki AI Fund never receives, holds or moves follower funds.",
  },
];

export default function MasterTrading() {
  return (
    <>
      <Seo
        title="Master Trading — Exchange-Hosted & Non-Custodial"
        path="/master-trading"
        description="Discover master trading opportunities hosted on supported external exchanges — with trader profiles, published performance information and clear risk disclosures."
      />

      <PageHero
        eyebrow="Offering 01 · Master Trading"
        titleLines={["Exchange-hosted", "master trading."]}
        intro="Enable your own account to discover master trading opportunities hosted on supported external exchanges — with transparent trader profiles, available performance and risk information, and a structure where your funds never leave your account."
        badges={["Non-custodial", "Exchange-hosted"]}
        image="https://images.pexels.com/photos/30704246/pexels-photo-30704246.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        imageAlt="Golden data trails flowing through a dark background"
      />

      {/* Overview + how it works */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14">
            <SectionHeading
              eyebrow="The Model"
              title={<>How exchange-hosted trading works</>}
              intro="Master trading mirrors a lead trader's positions onto follower accounts through the exchange's own copy-trading infrastructure. The exchange is the operator; we surface and explain the opportunity."
            />
            <Reveal delay={0.15} className="flex flex-col justify-center gap-5">
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                Because the entire relationship — allocation, mirroring, fees and redemption — runs
                on the host exchange, this model stays non-custodial for us and for you: your
                capital sits in your own exchange account at all times.
              </p>
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                What we contribute is curation and clarity: profiles, published risk data, plain
                explanations of the mechanics, and direct links to the exchange's own pages.
              </p>
              <div className="glass-gold rounded-2xl p-5 mt-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-400/80 mb-2">
                  Custody arrangement
                </p>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Non-custodial. Your assets remain in your own exchange account; the exchange is
                  the sole counterparty and operator of the copy-trading system.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-20">
            <ProcessFlow idPrefix="master" steps={STEPS} />
          </div>
        </div>
      </section>

      {/* Directory */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10 bg-ink-800/40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Master Trader Directory"
            title={<>Configured destinations, always labelled.</>}
            intro="Each card links to the master's page on the host exchange. Links are managed through this website's content configuration and can be updated at any time."
          />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {MASTER_TRADERS.map((mt) => (
              <StaggerItem key={mt.id} className="h-full">
                <article
                  data-testid={`master-trader-card-${mt.id}`}
                  className="glass rounded-3xl p-7 h-full flex flex-col hover:border-gold-400/35 transition-colors duration-500"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="px-3 py-1 rounded-full font-mono text-[9px] uppercase tracking-[0.18em] text-gold-200 border border-gold-400/25 bg-gold-400/[0.06]">
                      {mt.exchange}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-600">{mt.id.toUpperCase()}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-ink-50 mb-2">
                    {mt.name}
                  </h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold-400/70 mb-3">
                    {mt.style}
                  </p>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-7">{mt.description}</p>
                  <a
                    href={mt.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`master-trader-link-${mt.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-full border border-gold-400/35 bg-gold-400/[0.05] px-5 py-2.5 text-sm font-semibold text-gold-300 hover:bg-gold-400/[0.1] hover:border-gold-400/60 transition-all duration-300"
                  >
                    View on {mt.exchange}
                    <ArrowUpRight size={14} />
                  </a>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <p className="mt-8 text-xs text-zinc-600 font-mono">
              Placeholder destinations are shown for demonstration and must be replaced with the
              confirmed exchange pages before publication.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Key Differentiators"
            title={<>What this page gives you</>}
            align="center"
          />
          <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f) => (
              <StaggerItem key={f.title} className="h-full">
                <div className="glass rounded-2xl p-6 h-full hover:border-gold-400/30 transition-colors duration-500">
                  <f.icon size={20} className="text-gold-400 mb-5" />
                  <h3 className="text-sm font-semibold text-ink-50 mb-2">{f.title}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Risk + FAQ */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <RiskNote
            lines={[
              "Master trading is non-custodial: funds remain in your own exchange account, but they remain exposed to the host exchange as a counterparty and to the master trader's trading decisions.",
              "Copy-trading can produce losses faster than the underlying market. Review the exchange's own risk materials before allocating.",
            ]}
          />
          <div className="mt-16">
            <SectionHeading eyebrow="FAQ" title={<>Frequently asked questions</>} />
            <div className="mt-10">
              <FAQ items={FAQS.master} idPrefix="master-faq" />
            </div>
          </div>
        </div>
      </section>

      <CTABand
        testid="master-cta"
        title="Explore master traders on the host exchanges."
        subtitle="Direct links to the configured exchange destinations — or contact the team with any question first."
        primary={{ label: "Explore Master Traders", to: MASTER_TRADERS[0].url, external: true }}
        secondary={{ label: "Contact Our Team", to: "/contact" }}
      />
    </>
  );
}